import React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import SvgIcon from "@mui/material/SvgIcon";

// Иконка закладки
const BookmarkIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M17,3H7A2,2 0 0,0 5,5V21L12,18L19,21V5C19,4.89 18.7,4.67 18.56,4.62C18.41,4.56 18.23,4.6 18.1,4.72L17,3M17,18L12,15.82L7,18V5H17V18Z" />
  </SvgIcon>
);

const PREFIX = "BookmarkMarkers";

const classes = {
  container: `${PREFIX}-container`,
  marker: `${PREFIX}-marker`,
  icon: `${PREFIX}-icon`,
  arrow: `${PREFIX}-arrow`,
};

const Root = styled("div", {
  shouldForwardProp: (prop) => prop !== "placement",
})(({ theme, placement }) => ({
  [`&.${classes.container}`]: {
    position: "absolute",
    top: placement === "top" ? undefined : -25,
    bottom: placement === "bottom" ? -25 : undefined,
    left: 0,
    right: 0,
    height: 24,
    pointerEvents: "none",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.marker}`]: {
    position: "absolute",
    cursor: "pointer",
    pointerEvents: "auto",
    transition: "all 0.2s ease",
    transform: "translate(-50%, -50%)",
    top: "50%",
    display: "flex",
    flexDirection: placement === "top" ? "column-reverse" : "column",
    alignItems: "center",
    opacity: 0.9,
    "&:hover": {
      opacity: 1,
      transform: "translate(-50%, -50%) scale(1.1)",
      [`& .${classes.icon}`]: {
        backgroundColor: theme.palette.primary.light,
        boxShadow: `0 2px 12px rgba(0,0,0,0.2), 0 0 8px ${theme.palette.primary.main}40`,
      },
      [`& .${classes.icon} svg`]: {
        color: "white",
      },
    },
  },

  [`& .${classes.icon}`]: {
    width: 18,
    height: 18,
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
    marginBottom: 2,
    "& svg": {
      fontSize: 12,
      color: "white",
      transition: "color 0.2s ease",
    },
  },

  [`& .${classes.arrow}`]: {
    width: 0,
    height: 0,
    borderLeft: "3px solid transparent",
    borderRight: "3px solid transparent",
    ...(placement === "top"
      ? {
          borderBottom: `3px solid ${theme.palette.primary.main}`,
        }
      : {
          borderTop: `3px solid ${theme.palette.primary.main}`,
        }),
    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
  },
}));

/**
 * Компонент маркеров закладок на прогресс-баре
 * Показывает закладки как точки на LinearProgress
 * @param {string} placement - "top" для app bar (стрелка вверх), "bottom" для seekbar (стрелка вниз)
 */
function BookmarkMarkers({
  bookmarks,
  totalCharacters,
  onBookmarkClick,
  placement = "bottom",
}) {
  if (!totalCharacters || !bookmarks || bookmarks.length === 0) {
    return null;
  }

  // Фильтруем закладки, которые не выходят за границы книги
  const bookmarksToRender = bookmarks.filter(
    (bookmark) => bookmark.character <= totalCharacters
  );

  return (
    <Root className={classes.container} placement={placement}>
      {bookmarksToRender.map((bookmark) => {
        const leftPosition = (bookmark.character / totalCharacters) * 100;

        return (
          <Tooltip
            key={bookmark.id}
            title={
              <div>
                <div style={{ fontWeight: "bold" }}>
                  {bookmark.note || "Закладка без названия"}
                </div>
                {bookmark.chapterName && (
                  <div style={{ fontSize: "0.8em" }}>
                    {bookmark.chapterName}
                  </div>
                )}
                <div style={{ fontSize: "0.7em", opacity: 0.8 }}>
                  {bookmark.progressPercent?.toFixed(1)}% прочитано
                </div>
              </div>
            }
            placement={placement === "top" ? "bottom" : "top"}
            arrow
            PopperProps={{
              style: { zIndex: 9999 },
            }}
          >
            <div
              className={classes.marker}
              style={{ left: `${leftPosition}%` }}
              onClick={() => onBookmarkClick(bookmark)}
            >
              <div className={classes.icon}>
                <BookmarkIcon />
              </div>
              <div className={classes.arrow} />
            </div>
          </Tooltip>
        );
      })}
    </Root>
  );
}

BookmarkMarkers.propTypes = {
  bookmarks: PropTypes.array,
  totalCharacters: PropTypes.number.isRequired,
  onBookmarkClick: PropTypes.func.isRequired,
  placement: PropTypes.oneOf(["top", "bottom"]),
};

export default BookmarkMarkers;
